'use client';

import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { Button } from '@/components/ui/Button';
import Link from 'next/link';
import gsap from 'gsap';
import { Globe, ShieldCheck, Award } from 'lucide-react';

// Hero bileşeni - Tam ekran görsel ve ortalanmış içerik tasarımı
const Hero = () => {
  const heroRef = useRef<HTMLElement>(null);
  const bgLayerRef = useRef<HTMLDivElement>(null);
  const gradientRef = useRef<HTMLDivElement>(null);
  const subtextRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const quickLinksRef = useRef<HTMLDivElement>(null);
  const badgesRef = useRef<HTMLDivElement>(null);
  const floatingCardRef = useRef<HTMLDivElement>(null);
  const headingWords = ['Profesyonel', 'Berber ve', 'Kuaför', 'Mobilyaları'];

  useEffect(() => {
    const ctx = gsap.context(() => {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const hasFinePointer = window.matchMedia('(pointer:fine)').matches;
      const words = gsap.utils.toArray<HTMLElement>('.hero-word');
      const specialWord = gsap.utils.toArray<HTMLElement>('.hero-word-special');

      gsap.set(words, { opacity: 0, y: 40, filter: 'blur(10px)' });
      gsap.set(specialWord, { scale: 0.8, textShadow: '0 0 0 rgba(255,255,255,0)' });
      gsap.set(subtextRef.current, { opacity: 0, y: 20 });
      gsap.set(ctaRef.current, { opacity: 0, scale: 0.8 });
      gsap.set(quickLinksRef.current, { opacity: 0, y: 20 });
      gsap.set(badgesRef.current, { opacity: 0, y: 20 });
      gsap.set(floatingCardRef.current, { opacity: 0, x: -20, y: 20 });

      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.to(words, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.7,
        stagger: 0.1,
      })
        .to(
          specialWord,
          {
            scale: 1,
            duration: 0.55,
            textShadow: '0 0 16px rgba(255,255,255,0.65), 0 0 28px rgba(255,255,255,0.45)',
          },
          '-=0.15'
        )
        .to(
          subtextRef.current,
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
          },
          0.6
        )
        .to(
          ctaRef.current,
          {
            opacity: 1,
            scale: 1,
            duration: 0.6,
            ease: 'back.out(1.7)',
          },
          0.9
        )
        .to(
          quickLinksRef.current,
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
          },
          1.1
        )
        .to(
          badgesRef.current,
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
          },
          1.3
        )
        .to(
          floatingCardRef.current,
          {
            opacity: 1,
            x: 0,
            y: 0,
            duration: 0.6,
            ease: 'back.out(1.5)',
          },
          1.5
        );

      if (bgLayerRef.current && !prefersReducedMotion) {
        gsap.fromTo(
          bgLayerRef.current,
          { scale: 1 },
          { scale: 1.05, duration: 6, ease: 'none' }
        );
      }

      if (gradientRef.current) {
        gsap.fromTo(
          gradientRef.current,
          { opacity: 0.92 },
          { opacity: 0.75, duration: 1.2, ease: 'power2.out' }
        );
      }

      if (floatingCardRef.current && !prefersReducedMotion) {
        gsap.to(floatingCardRef.current, {
          y: -8,
          duration: 2,
          ease: 'sine.inOut',
          yoyo: true,
          repeat: -1,
          delay: 2.5
        });
      }

      const heroEl = heroRef.current;
      if (!heroEl || !bgLayerRef.current || prefersReducedMotion || !hasFinePointer) return;

      const quickX = gsap.quickTo(bgLayerRef.current, 'x', { duration: 0.45, ease: 'power2.out' });
      const quickY = gsap.quickTo(bgLayerRef.current, 'y', { duration: 0.45, ease: 'power2.out' });
      const moveHandler = (e: MouseEvent) => {
        const rect = heroEl.getBoundingClientRect();
        const mouseX = (e.clientX - rect.left) / rect.width - 0.5;
        const mouseY = (e.clientY - rect.top) / rect.height - 0.5;
        quickX(mouseX * 10);
        quickY(mouseY * 10);
      };
      const leaveHandler = () => {
        quickX(0);
        quickY(0);
      };

      heroEl.addEventListener('mousemove', moveHandler, { passive: true });
      heroEl.addEventListener('mouseleave', leaveHandler, { passive: true });
      return () => {
        heroEl.removeEventListener('mousemove', moveHandler);
        heroEl.removeEventListener('mouseleave', leaveHandler);
      };
    }, heroRef);

    return () => ctx.revert();
  }, []);

  // Arkaplan görseli (placeholder)
  const backgroundImage = '/images/headerfoto.webp';

  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);

  const badges = [
    { icon: Globe, text: 'Tüm Dünyaya Gönderim' },
    { icon: Award, text: '10+ Yıllık Tecrübe' },
    { icon: ShieldCheck, text: '%100 Memnuniyet' }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBadgeIndex((prev) => (prev + 1) % 3);
    }, 2000); // 2 saniyede bir değişim
    return () => clearInterval(timer);
  }, []);

  // Yazıp silme (typewriter) efekti için mesajlar ve durum yönetimi
  const messages = [
    "Premium berber tezgahları, berber koltukları, kuaför tezgahları ve güzellik salonu mobilyaları ile mekanınıza değer katın.",
    "Salonunuz için modern tasarımlar, ergonomik kuaför ekipmanları ve şık dekorasyon çözümleri sunuyoruz.",
    "Yıllardır, tecrübemiz ve kaliteli işçiliğimizle hayalinizdeki salonu gerçeğe dönüştürüyoruz."
  ];

  const [messageIndex, setMessageIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    const currentMessage = messages[messageIndex];

    if (!isDeleting) {
      if (displayedText.length < currentMessage.length) {
        timer = setTimeout(() => {
          setDisplayedText(currentMessage.substring(0, displayedText.length + 1));
        }, 30); // Yazma hızı (karakter başına ms)
      } else {
        // Yazma bittikten sonra 10 saniye bekle
        timer = setTimeout(() => {
          setIsDeleting(true);
        }, 10000);
      }
    } else {
      if (displayedText.length > 0) {
        timer = setTimeout(() => {
          setDisplayedText(displayedText.substring(0, displayedText.length - 1));
        }, 15); // Silme hızı (karakter başına ms)
      } else {
        setIsDeleting(false);
        setMessageIndex((prev) => (prev + 1) % messages.length);
      }
    }

    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, messageIndex]);

  // Aşağı kaydır fonksiyonu
  const handleScrollDown = () => {
    const el = document.getElementById('project-showcase');
    const headerHeight = 280; // Header yüksekliği (px) - daha yukarı hizalama için artırıldı
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      window.scrollTo({
        top: window.innerHeight,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section
      ref={heroRef}
      aria-label="Profesyonel Berber ve Kuaför Mobilyaları Ana Tanıtım Alanı"
      className="w-full max-w-none overflow-hidden relative flex items-center justify-center text-center min-h-[100svh] md:h-screen pt-28 md:pt-16 pb-4 md:pb-0"
      style={{ overflow: 'hidden' }}
    >
      {/* Arkaplan Görseli */}
      <div
        ref={bgLayerRef}
        className="hero-bg absolute inset-0 w-full h-full min-h-[300px] will-change-transform transform-gpu"
      >
        <Image
          src={backgroundImage}
          alt="Filiz Dekorasyon - Profesyonel berber koltukları, kuaför tezgahları ve salon mobilyaları showroom görünümü"
          fill
          sizes="100vw"
          className="object-cover"
          priority
          fetchPriority="high"
          quality={90}
        />
        {/* Görsel Üzerine Karartma ve Işık Efekti */}
        <div
          ref={gradientRef}
          className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/65 pointer-events-none"
        />
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-primary/20 to-transparent rounded-bl-full pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-primary/20 to-transparent rounded-tr-full pointer-events-none"></div>
      </div>

      {/* İçerik Bloğu - Ortalanmış */}
      <div
        className="relative z-10 text-white px-6 flex flex-col items-center justify-center w-full h-full"
      >
        {/* Başlık */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-4 drop-shadow-lg">
          <span className="sr-only">Lider Berber Koltukları, Berber Tezgahları, Kuaför Koltukları ve Kuaför Tezgahları Üreticisi</span>
          {headingWords.map((word, index) => (
            <span
              key={`${word}-${index}`}
              className={`hero-word inline-block mr-3 ${word === 'Kuaför' ? 'hero-word-special text-red-800' : ''}`}
            >
              {word}
            </span>
          ))}
        </h1>

        {/* Açıklama - Animasyonlu Yazıp Silme Efekti ve Düzenli Yükseklik */}
        <p
          ref={subtextRef}
          className="text-white text-base md:text-lg mb-8 opacity-100 drop-shadow-md min-h-[4.5rem] sm:min-h-[3rem] md:min-h-[2.5rem] flex items-center justify-center max-w-3xl"
        >
          <span>
            {displayedText}
            <span className="animate-pulse ml-0.5 text-primary font-bold">|</span>
          </span>
        </p>

        {/* Buton */}
        <div ref={ctaRef} className="flex flex-col sm:flex-row items-center gap-4">
          <Link href="/urunler">
            <Button
              size="lg"
              className="bg-primary text-white hover:bg-primary-dark transition-all duration-300 text-lg px-8 py-3 rounded-full shadow-lg hover:shadow-primary/40 transform hover:-translate-y-1"
            >
              Ürünleri Keşfet
            </Button>
          </Link>
          <Link href="/iletisim">
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent text-white border-white hover:bg-white/10 hover:text-white transition-all duration-300 text-lg px-8 py-3 rounded-full shadow-lg transform hover:-translate-y-1"
            >
              Fiyat Teklifi Al
            </Button>
          </Link>
        </div>

        {/* Hızlı Kategori Bağlantıları */}
        <div className="mt-8 md:mt-0 flex flex-wrap md:flex-col justify-center md:justify-center items-center md:items-start gap-2 md:gap-3 max-w-2xl md:absolute md:left-12 md:top-[62%] md:-translate-y-1/2 z-20 opacity-0" ref={quickLinksRef}>
          <span className="text-white/80 text-sm font-medium mr-2 md:mr-0 md:mb-2 self-center md:self-start md:text-white/60 md:text-xs md:uppercase md:tracking-wider md:font-semibold">Popüler:</span>
          {[
            { name: 'Berber Koltukları', slug: 'berber-koltuklari' },
            { name: 'Kuaför Tezgahları', slug: 'kuafor-tezgahlari' },
            { name: 'Yıkama Setleri', slug: 'yikama-setleri' }
          ].map(category => (
            <Link key={category.slug} href={`/urunler/kategori/${category.slug}`} title={`${category.name} kategorisine göz atın`}>
              <span className="text-xs md:text-sm px-4 py-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors cursor-pointer backdrop-blur-sm">
                {category.name}
              </span>
            </Link>
          ))}
        </div>


      </div>

      {/* Yüzen (Floating) İstatistik / Bilgi Kartı */}
      <div
        ref={floatingCardRef}
        className="hidden md:flex absolute bottom-8 left-8 z-30 bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-3 items-center gap-4 shadow-2xl hover:bg-black/50 transition-colors duration-300"
      >
        <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-white/5 flex-shrink-0">
          <Image
            src="/images/berbertezgahı.webp"
            alt="Berber Tezgahı 282 - Ayın en çok tercih edilen profesyonel berber tezgahı"
            fill
            className="object-cover"
          />
        </div>
        <div className="flex flex-col text-left">
          <div className="flex items-center gap-1 mb-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <svg key={star} className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <span className="text-white/70 text-xs font-medium uppercase tracking-wider">Ayın En Çok Tercih Edileni</span>
          <Link href="/urunler/berber-tezgahlari/berber-tezgah-181" className="text-white font-semibold text-sm hover:text-primary transition-colors mt-0.5 group flex items-center gap-1">
            Berber Tezgahı 181
            <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>

      {/* absolute: fixed, PageTransition içindeki transform yüzünden viewport'a göre değil yanlış kutuya göre konumlanıyordu */}
      <button
        type="button"
        onClick={handleScrollDown}
        className="absolute left-1/2 -translate-x-1/2 bottom-14 sm:bottom-10 z-20 flex flex-col items-center group animate-bounce"
        aria-label="Aşağı kaydır"
      >
        <svg className="w-10 h-10 text-white/80 group-hover:text-primary transition-colors duration-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
        <span className="text-xs text-white/70 mt-1">Aşağı Kaydır</span>
      </button>
    </section>
  );
};

export default Hero;